/*
 * tag-viewer.js
 * ---------------------------------------------------------------------------
 * Loads a .glb model into a transparent <canvas>, renders it as a flat fill
 * with detected edges drawn on top (silhouette + hard creases), and gently
 * tilts it toward the pointer (desktop) or the device's tilt (mobile).
 *
 * Plain-JS distillation of FillWithEdges.tsx: instead of fat LineSegments2 +
 * a clip-space outline shader, we use THREE.EdgesGeometry + a 1px
 * LineBasicMaterial sitting on top of a polygon-offset fill. Same idea, far
 * less machinery. Thresholds are exposed as data-attributes so they're easy
 * to tune from the Liquid snippet without touching this file.
 *
 * three.js is pulled from jsDelivr's `+esm` bundles (import URLs are already
 * rewritten to absolute paths) so no <script type="importmap"> is required.
 * The model is Draco-compressed, so DRACOLoader is wired to gstatic's decoder.
 */

import * as THREE from "https://cdn.jsdelivr.net/npm/three@0.160.0/+esm";
import { GLTFLoader } from "https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/loaders/GLTFLoader.js/+esm";
import { DRACOLoader } from "https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/loaders/DRACOLoader.js/+esm";

const DRACO_DECODER_PATH =
  "https://www.gstatic.com/draco/versioned/decoders/1.5.6/";

const DEG2RAD = Math.PI / 180;
const IS_DESKTOP =
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;
const PREFERS_REDUCED_MOTION =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// One DRACOLoader instance is reused across every viewer on the page.
let dracoLoader = null;
function getGLTFLoader() {
  if (!dracoLoader) {
    dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
  }
  const loader = new GLTFLoader();
  loader.setDRACOLoader(dracoLoader);
  return loader;
}

function num(el, name, fallback) {
  const v = parseFloat(el.dataset[name]);
  return Number.isFinite(v) ? v : fallback;
}

class TagViewer {
  constructor(container) {
    this.container = container;

    // ── Tunables (all overridable from the snippet via data-attributes) ──
    this.modelUrl = container.dataset.modelUrl;
    this.edgeThreshold = num(container, "edgeThreshold", 18); // degrees: edge kept when face angle exceeds this
    this.maxTilt = num(container, "tilt", 28) * DEG2RAD; // degrees of lean toward pointer / device
    this.damping = num(container, "damping", 0.08); // 0..1 lerp factor toward the target tilt each frame
    // Camera distance — smaller = bigger/zoomed in. Desktop can sit further out
    // (smaller model) than mobile; falls back to the mobile value if unset.
    const mobileDistance = num(container, "distance", 2.5);
    const desktopDistance = num(container, "distanceDesktop", mobileDistance);
    this.distance = IS_DESKTOP ? desktopDistance : mobileDistance;
    this.fitScale = num(container, "fitScale", 1); // extra size multiplier on the normalised model
    // Degrees of phone tilt that produce a full lean — smaller = more sensitive.
    this.gyroRange = num(container, "gyroRange", 15);
    this.edgeColor = new THREE.Color(container.dataset.edgeColor || "#1a1a1a");
    this.fillColor = new THREE.Color(container.dataset.fillColor || "#ffffff");
    this.fillOpacity = num(container, "fillOpacity", 1);
    // Canvas clear colour. 'transparent' keeps the alpha buffer; any hex fills it opaque.
    this.background = container.dataset.background || "#ffffff";
    // Base orientation so the model's front faces the camera at rest.
    this.baseRotation = new THREE.Euler(
      num(container, "baseRotationX", 0) * DEG2RAD,
      num(container, "baseRotationY", 0) * DEG2RAD,
      num(container, "baseRotationZ", 0) * DEG2RAD,
    );

    // Tilt state: current (rendered) vs target (driven by input).
    this.tilt = { x: 0, y: 0 };
    this.target = { x: 0, y: 0 };

    this.visible = true;
    this.running = false;
    this.disposed = false;

    this._initThree();
    this._loadModel();
    this._bindInput();
    this._observe();
  }

  _initThree() {
    const { clientWidth: w, clientHeight: h } = this.container;

    const transparent = this.background === "transparent";
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: transparent,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setSize(w || 1, h || 1, false);
    if (transparent) {
      this.renderer.setClearColor(0x000000, 0); // blends over whatever is behind
    } else {
      this.renderer.setClearColor(new THREE.Color(this.background), 1);
    }
    this.container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(
      35,
      (w || 1) / (h || 1),
      0.01,
      100,
    );
    this.camera.position.set(0, 0, this.distance);
    this.camera.lookAt(0, 0, 0);

    // Two nested groups so tilt stays view-aligned independent of base orientation:
    //   tiltGroup — rotated by the pointer/device in WORLD (screen) axes
    //     pivot   — holds the model's base orientation (so it faces us at rest)
    //       modelGroup (added in _buildFill) — the centred, scaled mesh
    this.tiltGroup = new THREE.Group();
    this.scene.add(this.tiltGroup);
    this.pivot = new THREE.Group();
    this.tiltGroup.add(this.pivot);
  }

  _loadModel() {
    getGLTFLoader().load(
      this.modelUrl,
      (gltf) => {
        if (this.disposed) return;
        const mesh = this._firstMesh(gltf.scene);
        if (!mesh) return;
        this._buildFill(mesh); // cheap: show the fill on the next frame
        this._frameModel();
        this.container.classList.add("tag-viewer--ready");
        this._start();
        this._buildEdgesDeferred(); // expensive: run edge detection during idle
      },
      undefined,
      (err) => console.error("[tag-viewer] failed to load model:", err),
    );
  }

  _firstMesh(root) {
    let found = null;
    root.traverse((o) => {
      if (!found && o.isMesh && o.geometry) found = o;
    });
    return found;
  }

  // Build the flat fill and add it to the scene. Cheap — runs before first paint.
  _buildFill(mesh) {
    this._geometry = mesh.geometry;
    // Centre the geometry about its own origin so scaling (in _frameModel) keeps
    // it centred. Both the fill and the deferred edges share this geometry.
    this._geometry.center();

    // Flat fill. polygonOffset pushes it slightly back in depth so the 1px edge
    // lines (which keep depthTest) sit cleanly on top without z-fighting —
    // same trick FillWithEdges uses.
    const fillMat = new THREE.MeshBasicMaterial({
      color: this.fillColor,
      transparent: this.fillOpacity < 1,
      opacity: this.fillOpacity,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });
    this.fill = new THREE.Mesh(this._geometry, fillMat);

    this.modelGroup = new THREE.Group();
    this.modelGroup.add(this.fill);
    this.pivot.add(this.modelGroup);

    this._fillMat = fillMat;
  }

  // Edge detection, deferred to idle time: keep an edge wherever the angle
  // between the two faces sharing it exceeds edgeThreshold (plus open-boundary
  // edges). On a dense mesh this is the most expensive step, so we run it off
  // the first-paint path to avoid stalling the page (and the bg grain canvas).
  _buildEdgesDeferred() {
    const run = () => {
      if (this.disposed || !this._geometry) return;
      const edgesGeom = new THREE.EdgesGeometry(
        this._geometry,
        this.edgeThreshold,
      );
      const lineMat = new THREE.LineBasicMaterial({
        color: this.edgeColor,
        transparent: true,
        depthWrite: false,
      });
      this.lines = new THREE.LineSegments(edgesGeom, lineMat);
      this.modelGroup.add(this.lines);
      this._edgesGeom = edgesGeom;
      this._lineMat = lineMat;
    };
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(run, { timeout: 1000 });
    } else {
      setTimeout(run, 0);
    }
  }

  // Normalise the (already-centred) model to ~1 unit, then let `distance` (the
  // camera position) control how large it appears — smaller distance = zoomed in.
  _frameModel() {
    this._geometry.computeBoundingBox();
    const size = this._geometry.boundingBox.getSize(new THREE.Vector3());

    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    this.modelGroup.scale.setScalar(this.fitScale / maxDim);

    this.camera.position.set(0, 0, this.distance);
    this.camera.lookAt(0, 0, 0);

    this.pivot.rotation.copy(this.baseRotation);
  }

  // ── Input: pointer on desktop, device orientation on mobile ──────────────
  _bindInput() {
    if (PREFERS_REDUCED_MOTION) return;

    if (IS_DESKTOP) {
      this._onPointerMove = (e) => {
        const r = this.container.getBoundingClientRect();
        const nx = ((e.clientX - r.left) / r.width) * 2 - 1; // -1..1
        const ny = ((e.clientY - r.top) / r.height) * 2 - 1;
        this.target.y = THREE.MathUtils.clamp(nx, -1, 1) * this.maxTilt; // yaw toward cursor X
        this.target.x = THREE.MathUtils.clamp(ny, -1, 1) * this.maxTilt; // pitch toward cursor Y
      };
      this._onPointerLeave = () => {
        this.target.x = 0;
        this.target.y = 0;
      };
      window.addEventListener("pointermove", this._onPointerMove, {
        passive: true,
      });
      this.container.addEventListener("pointerleave", this._onPointerLeave);
    } else {
      this._setupDeviceOrientation();
    }
  }

  _setupDeviceOrientation() {
    if (typeof DeviceOrientationEvent === "undefined") return;

    this._orient0 = null; // first reading is captured as the neutral pose
    this._onOrient = (e) => {
      if (e.beta == null || e.gamma == null) return;
      if (!this._orient0) this._orient0 = { beta: e.beta, gamma: e.gamma };
      const dBeta = (e.beta - this._orient0.beta) / this.gyroRange; // gyroRange deg = full lean
      const dGamma = (e.gamma - this._orient0.gamma) / this.gyroRange;
      this.target.x = THREE.MathUtils.clamp(dBeta, -1, 1) * this.maxTilt;
      this.target.y = THREE.MathUtils.clamp(dGamma, -1, 1) * this.maxTilt;
    };

    const attach = () =>
      window.addEventListener("deviceorientation", this._onOrient, {
        passive: true,
      });

    // iOS 13+ gates motion behind a permission prompt that must be triggered by
    // a user gesture — and only `touchend`/`click` reliably count (NOT
    // pointerdown/touchstart). We keep listening on those until permission is
    // actually granted, then tear the listeners down. Crucially we do NOT remove
    // the listeners on a failed/early attempt, so a tap always gets another try.
    if (typeof DeviceOrientationEvent.requestPermission === "function") {
      const gestures = ["touchend", "click"];
      let granted = false;
      const cleanup = () =>
        gestures.forEach((ev) =>
          window.removeEventListener(ev, this._requestMotion),
        );
      this._requestMotion = () => {
        if (granted) return;
        DeviceOrientationEvent.requestPermission()
          .then((state) => {
            if (state === "granted") {
              granted = true;
              cleanup();
              attach();
            }
          })
          .catch(() => {}); // not a valid gesture / dismissed — keep listening
      };
      this._cleanupMotionGesture = cleanup;
      gestures.forEach((ev) =>
        window.addEventListener(ev, this._requestMotion),
      );
    } else {
      attach();
    }
  }

  // Pause rendering when scrolled out of view or the tab is hidden.
  _observe() {
    this._io = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) this._start();
        else this._stop();
      },
      { threshold: 0.01 },
    );
    this._io.observe(this.container);

    this._ro = new ResizeObserver(() => this._resize());
    this._ro.observe(this.container);

    this._onVisibility = () => {
      if (document.hidden) this._stop();
      else if (this.visible) this._start();
    };
    document.addEventListener("visibilitychange", this._onVisibility);
  }

  _resize() {
    const { clientWidth: w, clientHeight: h } = this.container;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  _start() {
    if (this.running || this.disposed || !this.scene) return;
    this.running = true;
    this._loop = () => {
      if (!this.running) return;
      this._frame();
      this._raf = requestAnimationFrame(this._loop);
    };
    this._raf = requestAnimationFrame(this._loop);
  }

  _stop() {
    this.running = false;
    if (this._raf) cancelAnimationFrame(this._raf);
  }

  _frame() {
    // Ease current tilt toward the target. Applied to tiltGroup (world/screen
    // axes), so the lean is always toward the pointer regardless of how the
    // model is oriented inside by baseRotation.
    this.tilt.x += (this.target.x - this.tilt.x) * this.damping;
    this.tilt.y += (this.target.y - this.tilt.y) * this.damping;
    this.tiltGroup.rotation.x = this.tilt.x;
    this.tiltGroup.rotation.y = this.tilt.y;
    this.renderer.render(this.scene, this.camera);
  }

  dispose() {
    this.disposed = true;
    this._stop();
    if (this._onPointerMove)
      window.removeEventListener("pointermove", this._onPointerMove);
    if (this._onOrient)
      window.removeEventListener("deviceorientation", this._onOrient);
    if (this._cleanupMotionGesture) this._cleanupMotionGesture();
    if (this._onVisibility)
      document.removeEventListener("visibilitychange", this._onVisibility);
    if (this._io) this._io.disconnect();
    if (this._ro) this._ro.disconnect();
    this._edgesGeom?.dispose();
    this._fillMat?.dispose();
    this._lineMat?.dispose();
    this.renderer?.dispose();
    this.renderer?.domElement?.remove();
  }
}

function init() {
  document.querySelectorAll("[data-tag-viewer]").forEach((el) => {
    if (el._tagViewer || !el.dataset.modelUrl) return;
    el._tagViewer = new TagViewer(el);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
